import React, { FC, useEffect, useRef, useState } from 'react';
import './FileUploader.scss';
import Button from '@/components/shared/Button';
import { Snackbar, Alert, AlertColor } from '@mui/material';

const baseClass = 'FileUploader';

// Parse environment variable for max file size, with a fallback
const maxFileSizeMB = parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB || '100', 10);
const defaultMaxFileSize = maxFileSizeMB * 1024 * 1024; // Convert MB to bytes


/**
 * Notification type
 */
type NotificationType = {
  message: string;
  severity: AlertColor;
};

/**
 * Interface for file upload component
 * @property {Function} onFilesSelect - Callback function when files are selected
 * @property {string[]} acceptFiles - List of acceptable MIME file types
 * @property {number} filesLimit - Maximum number of uploadable files
 * @property {number} maxFileSize - Maximum file size in bytes
 * @property {string} className - Additional CSS classes
 * @property {File[] | File | null} files - Existing files
 * @property {string} filesDescription - Description of acceptable files for user
 * @property {boolean} replaceFilesOnAdd - Replace existing files when adding new ones
 * @property {Function} onError - Callback function for errors
 * @property {boolean} useToasts - Whether to use built-in toasts for notifications
 * @property {number} toastDuration - Toast display duration in milliseconds
 */
interface FileUploaderProps {
  onFilesSelect: (files: File[]) => void;
  acceptFiles?: string[];
  filesLimit?: number;
  maxFileSize?: number;
  className?: string;
  files: File[] | File | null;
  filesDescription?: string;
  replaceFilesOnAdd?: boolean;
  onError?: (message: string) => void;
  useToasts?: boolean;
  toastDuration?: number;
}

/**
 * Interface for individual file in list
 */
interface FileItemProps {
  file: File;
  onRemove: () => void;
  baseClass: string;
}

/**
 * Component for displaying individual file in list
 */
const FileItem: FC<FileItemProps> = ({ file, onRemove, baseClass }) => {
  return (
    <div className={`${baseClass}__file`} data-testid="file-item">
      <span className={`${baseClass}__file-name`}>{file.name}</span>
      <button
        type="button"
        className={`${baseClass}__file-remove`}
        onClick={onRemove}
        aria-label={`Remove file ${file.name}`}
        data-testid="remove-file-button"
      >
        <i className="fa-solid fa-trash-can" aria-hidden="true"></i>
      </button>
    </div>
  );
};

/**
 * Component for file upload with drag-and-drop support
 */
const FileUploader: FC<FileUploaderProps> = ({
                                               onFilesSelect,
                                               acceptFiles = [],
                                               filesLimit = 1,
                                               maxFileSize = defaultMaxFileSize,
                                               className = '',
                                               files = [],
                                               filesDescription = '',
                                               replaceFilesOnAdd = false,
                                               onError = (message) => console.error(message),
                                               useToasts = true,
                                               toastDuration = 5000,
                                             }) => {
  const [filesState, setFilesState] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [notification, setNotification] = useState<NotificationType | null>(
    null,
  );

  // Synchronize file state with props
  useEffect(() => {
    if (!files) {
      setFilesState([]);
    } else if (files && Array.isArray(files)) {
      setFilesState(files);
    } else {
      setFilesState([files]);
    }
  }, [files]);

  /**
   * Checks if file type is supported
   * @param fileType File MIME type
   * @returns Boolean - whether type is supported
   */
  const isFileTypeSupported = (fileType: string): boolean => {
    const allowedTypes =
      acceptFiles.length > 0
        ? acceptFiles
        : [
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
          'text/csv', // .csv
        ];

    return allowedTypes.includes(fileType);
  };

  /**
   * Formats file size to readable format
   * @param bytes size in bytes
   * @returns formatted size
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Show notification
   * @param message Message text
   * @param severity Notification type (error, warning, info, success)
   */
  const showNotification = (
    message: string,
    severity: AlertColor = 'error',
  ) => {
    setNotification({ message, severity });

    // Also call onError callback for message handling at parent component level
    if (severity === 'error') {
      onError(message);
    }
  };

  /**
   * Close notification
   */
  const handleCloseNotification = () => {
    setNotification(null);
  };

  /**
   * Handle selected files
   * @param files FileList with selected files
   */
  const handleFile = (files: FileList) => {
    const selectedFiles = Array.from(files).filter((file) => {
      // Check file size
      if (file.size > maxFileSize) {
        const errorMsg = `File ${file.name} exceeds maximum size ${formatFileSize(maxFileSize)}`;
        showNotification(errorMsg, 'error');
        return false;
      }

      // Check file MIME type
      if (!isFileTypeSupported(file.type)) {
        const errorMsg = `Unsupported file type: ${file.name}`;
        showNotification(errorMsg, 'error');
        return false;
      }

      return true;
    });

    if (selectedFiles.length === 0) return;

    // If file replacement is enabled and limit is 1
    if (replaceFilesOnAdd && filesLimit === 1) {
      setFilesState([selectedFiles[0]]);
      onFilesSelect([selectedFiles[0]]);
      showNotification(
        `File ${selectedFiles[0].name} successfully added`,
        'success',
      );
      return;
    }

    // Check file count limit
    if (filesState.length + selectedFiles.length > filesLimit) {
      showNotification(
        `You can upload no more than ${filesLimit} files`,
        'warning',
      );
      return;
    }

    const updatedFiles = [...filesState, ...selectedFiles];
    setFilesState(updatedFiles);
    onFilesSelect(updatedFiles);

    // Show success message
    if (selectedFiles.length === 1) {
      showNotification(
        `File ${selectedFiles[0].name} successfully added`,
        'success',
      );
    } else {
      showNotification(`Added ${selectedFiles.length} files`, 'success');
    }
  };

  /**
   * Handle drag over event on upload area
   */
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  /**
   * Handle drag leave event from upload area
   */
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  /**
   * Handle drop event in upload area
   */
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFile(event.dataTransfer.files);
    }
  };

  /**
   * Handle file selection through dialog
   */
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFile(event.target.files);
    }
  };

  /**
   * Handle file removal
   * @param index index of file to remove
   */
  const handleRemoveFile = (index: number) => {
    const removedFile = filesState[index];
    const updatedFiles = filesState.filter((_, i) => i !== index);
    setFilesState(updatedFiles);
    onFilesSelect(updatedFiles);

    // Display file removal notification
    if (removedFile) {
      showNotification(`File ${removedFile.name} removed`, 'info');
    }
  };

  /**
   * Opens file selection dialog
   */
  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <div
        className={`${baseClass} ${dragActive ? 'drag-active' : ''} ${className}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="file-uploader"
        role="region"
        aria-label="File upload"
      >
        <div className={`${baseClass}__content`}>
          <p className={`${baseClass}__text`}>
            Drag and drop document here <br /> <span>or</span>
          </p>
          <input
            type="file"
            ref={fileInputRef}
            accept={acceptFiles.join(',')}
            onChange={handleFileSelect}
            hidden
            multiple={filesLimit > 1}
            aria-label="Select file for upload"
            data-testid="file-input"
          />
          <Button
            onClick={openFileDialog}
            className={`${baseClass}__btn`}
            data-testid="select-file-button"
          >
            Browse Files
          </Button>
          <p className={`${baseClass}__subtext`} data-testid="file-description">
            {filesDescription || `XLS, CSV / max ${maxFileSizeMB} MB`}
          </p>

          {filesState.length > 0 && (
            <div className={`${baseClass}__files`} data-testid="files-list">
              {filesState.map((file, index) => (
                <FileItem
                  key={`${file.name}-${index}`}
                  file={file}
                  onRemove={() => handleRemoveFile(index)}
                  baseClass={baseClass}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Notification system using MUI Snackbar */}
      {useToasts && notification && (
        <Snackbar
          open={Boolean(notification)}
          autoHideDuration={toastDuration}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          data-testid="notification-toast"
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default FileUploader;